import { useMutation } from '@apollo/client'
import { Formik } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import Modal from 'components/v2/Modal'
import { IUploadCsvFormValues } from 'features/OperationalCosts/types'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { UPLOAD_OPERATIONAL_COST_SETTINGS } from 'features/OperationalCosts/mutations'
import { schema } from './schema'
import { getUnprocessableErrors } from 'utils/error'

const messages = defineMessages({
  businessClientPlaceholder: {
    id: 'partnerCost.operationalCosts.form.businessClient.placeholder',
    defaultMessage: 'Select Business Client'
  },
  businessSitePlaceholder: {
    id: 'partnerCost.operationalCosts.form.businessSite.placeholder',
    defaultMessage: 'Select Business Site'
  },
  urlPlaceholder: {
    id: 'partnerCost.operationalCosts.form.url.placeholder',
    defaultMessage: 'Enter the link to the CSV file'
  }
})

interface UploadCsvModalProps {
  onClose: () => void
}

export const UploadCSVModal = ({ onClose }: UploadCsvModalProps): ReactElement => {
  const intl = useIntl()
  const [uploadOperationalCostSettings, { loading, error }] = useMutation(UPLOAD_OPERATIONAL_COST_SETTINGS)

  const initialFormValues: IUploadCsvFormValues = {
    url: '',
    businessClient: null,
    businessSite: null
  }

  const handleSubmit = async (formValues: IUploadCsvFormValues): Promise<void> => {
    try {
      await uploadOperationalCostSettings({
        variables: {
          input: {
            sheetUrl: formValues.url,
            businessClientID: formValues.businessClient?.id || undefined,
            businessSiteID: formValues.businessSite?.id || undefined
          }
        }
      })
      onClose()
    } catch (error) {
      // Do nothing
    }
  }

  return (
    <Modal.Container onClose={onClose}>
      <Modal.Header smallMarginBottom>
        <Heading.H5>
          <FormattedMessage id="operationalCosts.uploadCsv.title" defaultMessage="Upload Settings" />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialFormValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {({ handleSubmit, values, handleChange, handleBlur, setValues, touched, errors }): ReactElement => {
          return (
            <form onSubmit={handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <BusinessClientAutocomplete
                      id="businessClient"
                      value={values.businessClient?.name || ''}
                      onChange={(businessClient): void => {
                        setValues({
                          ...values,
                          businessClient: businessClient?.id ? businessClient : null,
                          businessSite: null
                        })
                      }}
                      placeholder={intl.formatMessage(messages.businessClientPlaceholder)}
                      label={
                        <FormattedMessage
                          id="operationalCosts.form.businessClient.label"
                          defaultMessage="Business Client"
                        />
                      }
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <BusinessSiteAutocomplete
                      id="businessSite"
                      label={
                        <FormattedMessage
                          id="operationalCosts.form.businessSite.label"
                          defaultMessage="Business Site"
                        />
                      }
                      placeholder={intl.formatMessage(messages.businessSitePlaceholder)}
                      value={values.businessSite?.name || ''}
                      onChange={(businessSite): void => {
                        setValues({
                          ...values,
                          businessSite: businessSite?.id ? businessSite : null
                        })
                      }}
                      businessClientId={values.businessClient?.id}
                      disabled={!values.businessClient?.id}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Input
                      id="url"
                      name="url"
                      label={<FormattedMessage id="operationalCosts.uploadCsv.form.url" defaultMessage="Link" />}
                      placeholder={intl.formatMessage(messages.urlPlaceholder)}
                      value={values.url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.url}
                      errors={errors.url}
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                <Modal.ErrorMessage>
                  {error && (
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="operationalCosts.uploadCsv.form.error"
                        defaultMessage="There was an error while creating your settings"
                      />
                    </ErrorMessage>
                  )}
                </Modal.ErrorMessage>
                <Modal.Actions>
                  <Button kind="cancel" onClick={(): void => onClose()}>
                    <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button type="submit" kind="primary" loading={loading}>
                    <FormattedMessage id="dictionary.upload" defaultMessage="Upload" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
